export const START_AT_DATE_STRING_REGEX = /^\d{4}-\d{2}-\d{2}$/;

export interface ContractTypesInterface {
  label: string;
  value: string;
}

export interface CategoryOptionsInterface {
  label: string;
  value: string;
}

export interface CourseSegmentOptionsInterface {
  label: string;
  value: CourseSegment;
}

export enum CourseEnrolmentHealthStatusKey {
  AT_RISK = 'AT_RISK',
  OVERDUE = 'OVERDUE',
  ON_TRACK = 'ON_TRACK',
  WITHDRAWN = 'WITHDRAWN',
}

export type CourseEnrolmentHealthStatusItem = {
  label: string;
  colorScheme: string;
};
export const CourseEnrolmentHealthStatus: Record<
  CourseEnrolmentHealthStatusKey,
  CourseEnrolmentHealthStatusItem
> = {
  [CourseEnrolmentHealthStatusKey.AT_RISK]: {
    label: 'At risk',
    colorScheme: 'red',
  },
  [CourseEnrolmentHealthStatusKey.OVERDUE]: {
    label: 'Overdue',
    colorScheme: 'orange',
  },
  [CourseEnrolmentHealthStatusKey.ON_TRACK]: {
    label: 'On track',
    colorScheme: 'green',
  },
  [CourseEnrolmentHealthStatusKey.WITHDRAWN]: {
    label: 'Withdrawn',
    colorScheme: 'gray',
  },
} as const;

export const getCourseEnrolmentHealthLabels: string[] = Object.values(
  CourseEnrolmentHealthStatus,
).map((status) => status.label);

export function getCourseEnrolmentHealthByLabel(
  label: string,
): CourseEnrolmentHealthStatusItem | undefined {
  return Object.values(CourseEnrolmentHealthStatus).find((status) => status.label === label);
}

export type CourseEnrolmentHealthStatusType =
  (typeof CourseEnrolmentHealthStatus)[keyof typeof CourseEnrolmentHealthStatus];

export type CourseEnrolmentHealthLabelsType = CourseEnrolmentHealthStatusType['label'];

export enum CourseEnrolmentProgressKey {
  NOT_STARTED = 'NOT_STARTED',
  LT_25 = 'LT_25',
  GTE_25_74_LTE = 'GTE_25_74_LTE',
  GTE_75 = 'GTE_75',
  COMPLETED = 'COMPLETED',
}
export type CourseEnrolmentProgressItem = {
  label: string;
  urlQueryParam: CourseEnrolmentProgressKey;
};
export const CourseEnrolmentProgress: Record<
  CourseEnrolmentProgressKey,
  CourseEnrolmentProgressItem
> = {
  [CourseEnrolmentProgressKey.NOT_STARTED]: {
    label: 'Not started',
    urlQueryParam: CourseEnrolmentProgressKey.NOT_STARTED,
  },
  [CourseEnrolmentProgressKey.LT_25]: {
    label: 'In progress: <25%',
    urlQueryParam: CourseEnrolmentProgressKey.LT_25,
  },
  [CourseEnrolmentProgressKey.GTE_25_74_LTE]: {
    label: 'In progress: 25-74%',
    urlQueryParam: CourseEnrolmentProgressKey.GTE_25_74_LTE,
  },
  [CourseEnrolmentProgressKey.GTE_75]: {
    label: 'In progress: >75%',
    urlQueryParam: CourseEnrolmentProgressKey.GTE_75,
  },
  [CourseEnrolmentProgressKey.COMPLETED]: {
    label: 'Completed',
    urlQueryParam: CourseEnrolmentProgressKey.COMPLETED,
  },
} as const;

interface CourseEnrolmentProgressOption {
  label: string;
  value: CourseEnrolmentProgressKey;
}

export const getCourseEnrolmentProgressOptions: CourseEnrolmentProgressOptions = Object.entries(
  CourseEnrolmentProgress,
).map(([_key, { label, urlQueryParam }]) => ({
  label,
  value: urlQueryParam,
}));

export function getCourseEnrolmentProgressByLabel(
  label: string,
): CourseEnrolmentProgressItem | undefined {
  return Object.values(CourseEnrolmentProgress).find((progress) => progress.label === label);
}

export type CourseEnrolmentProgressOptions = CourseEnrolmentProgressOption[];

/**
 * All contract types supported by the system.
 */
export const ContractTypes: ContractTypesInterface[] = [
  {
    label: 'Workforce Australia',
    value: 'Workforce Australia',
  },
  {
    label: 'Inclusive Employment Australia (IEA)',
    value: 'IEA',
  },
  {
    label: 'Transition to Work (TTW)',
    value: 'TTW',
  },
  {
    label: 'Parents Pathways',
    value: 'Parents Pathways',
  },
  {
    label: 'Other',
    value: 'Other',
  },
];

export function getCategoryOptions(contractType?: string): CategoryOptionsInterface[] {
  switch (contractType) {
    case 'Workforce Australia':
      return [
        {
          label: 'Progression',
          value: 'Progression',
        },
        {
          label: 'Mandatory',
          value: 'Mandatory',
        },
        {
          label: 'Activation',
          value: 'Activation',
        },
        {
          label: 'Other',
          value: 'Other',
        },
      ];
    case 'IEA':
      return [
        {
          label: 'Principal Carer',
          value: 'Principal Carer',
        },
        {
          label: 'Early School Leaver',
          value: 'Early School Leaver',
        },
        {
          label: 'DSP Recipients',
          value: 'DSP Recipients',
        },
        {
          label: 'Other',
          value: 'Other',
        },
      ];
    case 'TTW':
      return [
        {
          label: 'Early School Leaver',
          value: 'Early School Leaver',
        },
        {
          label: 'Principal Carer',
          value: 'Principal Carer',
        },
        {
          label: 'Other',
          value: 'Other',
        },
      ];
    case 'Parents Pathways':
    case 'Parents Next':
      return [
        {
          label: 'Other - Public',
          value: 'Other - Public',
        },
      ];
    case undefined:
      return [];
    default:
      return [];
  }
}

export enum WorkSchedule {
  FULL_TIME = 'FULL_TIME',
  PART_TIME = 'PART_TIME',
}

export function isWorkSchedule(workSchedule: string): workSchedule is WorkSchedule {
  // SAFETY: We are only asserting that the workSchedule is a valid WorkSchedule
  // if it matches a value in the WorkSchedule enum
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return Object.values(WorkSchedule).includes(workSchedule as WorkSchedule);
}

export enum CourseSegment {
  FULL_COURSE = 'Full course',
  FIRST_SEMESTER_ONLY = 'First semester only',
}

export function isCourseSegment(segment: string): segment is CourseSegment {
  // SAFETY: We are only asserting that the segment is a valid CourseSegment
  // if it matches a value in the CourseSegment enum
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return Object.values(CourseSegment).includes(segment as CourseSegment);
}

export const CourseSegmentOptions: CourseSegmentOptionsInterface[] = [
  {
    label: CourseSegment.FULL_COURSE,
    value: CourseSegment.FULL_COURSE,
  },
  {
    label: CourseSegment.FIRST_SEMESTER_ONLY,
    value: CourseSegment.FIRST_SEMESTER_ONLY,
  },
];

// TODO: it's unclear why this map is needed as the keys and values match.
// so any lookup in the map for a value, could be replaced with the value itself.
export const CourseReferralSegmentMap = {
  'Full course': 'Full course',
  FULL_COURSE: 'Full course',
  'First semester only': 'First semester only',
  FIRST_SEMESTER_ONLY: 'First semester only',
};

// TODO: Need to rename - Internal Enrolments can be either Skill sets or full qualifications
export enum SkillSetContractCategories {
  STUDENT = 'Other - Skill set',
  INTERNAL_ENROLMENTS = 'Internal enrolment',
}

export function getProgressStatusText(progress_status: string): {
  variant: string;
  colorScheme: string;
  text: string;
} | null {
  let variant = 'outline';
  let colorScheme = 'gray';
  let text = ``;

  if (progress_status === 'Not started') {
    text = 'Not started';
  }
  if (progress_status === 'Completed') {
    variant = 'solid';
    colorScheme = 'green';
    text = 'Completed';
  } else if (progress_status === 'Withdrawn') {
    variant = 'solid';
    colorScheme = 'gray';
    text = 'Withdrawn';
  } else if (progress_status === 'Deleted') {
    variant = 'solid';
    colorScheme = 'gray';
    text = 'Deleted';
  }

  if (!text) {
    return null;
  }

  return {
    variant,
    colorScheme,
    text,
  };
}

/**
 * Type guard for SkillSetContractCategories
 */
export function isSkillSetContractCategory(value: unknown): value is SkillSetContractCategories {
  if (typeof value !== 'string') {
    return false;
  }

  return (Object.values(SkillSetContractCategories) as readonly string[]).includes(value);
}
